import React, { useEffect, useState } from "react";
import { getBoardPostDetailByPublicId, getBoardPosts } from "../../services/contactCenterService";
import { getI18nLanguageFromLocalStorage } from "../../helpers/localStorageHelper";
import { getLocalizedField } from "../../helpers/localizedHelper";
import { useTranslation } from "react-i18next";
import AttachmentDownloads from "./AttachmentDownloads";

// English fallbacks for this component's common_ns keys. The backend common_ns source
// (Google Sheet -> ContentTranslation) overrides these once the keys are seeded; ko/vi
// are served from there. Keeps the component self-sufficient without a local bundle.
const DEFAULTS = {
  "CONTACT_US_CENTER.COMMON.LOADING": "Loading...",
  "CONTACT_US_CENTER.DETAIL.AUTHOR_LABEL": "Author",
  "CONTACT_US_CENTER.DETAIL.AUTHOR_DEFAULT": "Operations Team",
  "CONTACT_US_CENTER.DETAIL.DATE_LABEL": "Date",
  "CONTACT_US_CENTER.DETAIL.VIEWS_LABEL": "Views",
  "CONTACT_US_CENTER.DETAIL.BACK": "List",
  "CONTACT_US_CENTER.DETAIL.PREV": "Previous",
  "CONTACT_US_CENTER.DETAIL.NEXT": "Next",
};

// Sibling posts are resolved from the tab's full list, so cap how many we scan.
const SIBLING_SCAN_ROWS = 500;

/** Board post detail (제목 / 작성일 / 조회수 / 본문 / 첨부파일). Loaded by the post's public GUID. */
const BoardPostDetail = ({ group, publicId, onBack, onOpenPost }) => {
  const [post, setPost] = useState(null);
  // Prev/next post within the same tab, following the list ordering (pinned first, newest first).
  const [nav, setNav] = useState({ prev: null, next: null });
  const language = getI18nLanguageFromLocalStorage();
  const { t: translate } = useTranslation("common_ns", { useSuspense: false });
  const t = (key, options) =>
    translate(key, { defaultValue: DEFAULTS[key] ?? key, ...options });

  useEffect(() => {
    let alive = true;
    getBoardPostDetailByPublicId(group, publicId).then((res) => {
      if (alive) setPost(res);
    });
    return () => {
      alive = false;
    };
  }, [group, publicId]);

  // Resolve prev/next from the tab's ordered post list once the post (and its tabId) is known.
  // Self-fetched here (not passed from the list) so deep-links / refreshes still get navigation.
  useEffect(() => {
    if (!post?.tabId) return;
    let alive = true;
    getBoardPosts(group, post.tabId, { first: 0, rows: SIBLING_SCAN_ROWS }).then((res) => {
      if (!alive) return;
      const list = res.data || [];
      const idx = list.findIndex((p) => p.publicId === publicId);
      setNav({
        // "이전글" = the entry above in the list; "다음 글" = the entry below.
        prev: idx > 0 ? list[idx - 1] : null,
        next: idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null,
      });
    });
    return () => {
      alive = false;
    };
  }, [group, post?.tabId, publicId]);

  if (!post) {
    return <div className="cuc-empty">{t("CONTACT_US_CENTER.COMMON.LOADING")}</div>;
  }

  const title = getLocalizedField(post, "title", language);
  const content = getLocalizedField(post, "content", language);
  const attachments = post.attachments || [];
  const author = post.author || t("CONTACT_US_CENTER.DETAIL.AUTHOR_DEFAULT");

  return (
    <div className="cuc-detail">
      <h2 className="cuc-detail-title">{title}</h2>
      <div className="cuc-detail-meta">
        <span>{t("CONTACT_US_CENTER.DETAIL.AUTHOR_LABEL")}: {author}</span>
        <span>{t("CONTACT_US_CENTER.DETAIL.DATE_LABEL")}: {(post.createdTime || "").slice(0, 10)}</span>
        <span>{t("CONTACT_US_CENTER.DETAIL.VIEWS_LABEL")}: {post.viewCount}</span>
      </div>

      <div
        className="cuc-detail-body"
        // Content is sanitized HTML produced by the admin editor.
        dangerouslySetInnerHTML={{ __html: content || "" }}
      />

      <AttachmentDownloads attachments={attachments} title={title} />

      <div className="cuc-detail-nav">
        <button
          type="button"
          className="cuc-detail-nav-side"
          onClick={() => nav.prev && onOpenPost(nav.prev.publicId)}
          disabled={!nav.prev}
        >
          <i className="pi pi-angle-left" />
          <span>{t("CONTACT_US_CENTER.DETAIL.PREV")}</span>
        </button>

        <button type="button" className="cuc-detail-nav-list" onClick={onBack}>
          {t("CONTACT_US_CENTER.DETAIL.BACK")}
        </button>

        <button
          type="button"
          className="cuc-detail-nav-side"
          onClick={() => nav.next && onOpenPost(nav.next.publicId)}
          disabled={!nav.next}
        >
          <span>{t("CONTACT_US_CENTER.DETAIL.NEXT")}</span>
          <i className="pi pi-angle-right" />
        </button>
      </div>
    </div>
  );
};

export default BoardPostDetail;
